import { classnames } from 'Utils/classnames/classnames.utils';
import { get } from 'lodash';
import type { FieldProps, FormikContextType } from 'formik';
import 'formik';
import { IS_INVALID, IS_VALID } from 'Components/frameworks/form-validator-hoc/validationStates';
import * as yup from 'yup';
export const getValidationStatus = (
  { name, value }: FieldProps['field'],
  { touched, errors, submitCount }: FieldProps['form'],
  isDisabled: boolean = false,
) => {
  const hadInteraction = get(touched, name) || submitCount > 0;
  const validationState = get(errors, name) ? IS_INVALID : IS_VALID;
  const isValidated = hadInteraction && validationState === IS_VALID && value;
  const isErrored = hadInteraction && validationState === IS_INVALID;
  return {
    isDefaultState: !isValidated && !isErrored && !isDisabled,
    isErrored,
    isTouched: hadInteraction,
    isValidated,
  };
};

// eslint-disable-next-line no-promise-executor-return
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const getValidateFieldFunction =
  (validator: yup, context: FormikContextType<unknown> | null | undefined, locale?: string) =>
  async (val: string) => {
    let validationResult = '';

    if (locale === 'ja-JP') {
      // We need to do this because of this issue https://github.com/ClubMediterranee/cm-b2c/issues/21679
      // Languages with composition make the input disappear after the composition ends
      // So we need to field to await before validating
      await sleep(300).then(async () => {
        try {
          await validator.validateSync(val, {
            context,
          });
        } catch (e) {
          validationResult = e.message;
        }
      });
    } else {
      try {
        await validator.validateSync(val, {
          context,
        });
      } catch (e) {
        validationResult = e.message;
      }
    }

    return validationResult;
  };
export const focusNextInput = (target: HTMLInputElement) => {
  const nextRef = target.nextSibling;

  if (target.value.length === target.maxLength && nextRef) {
    (nextRef as HTMLElement).focus();
  }
};
export const getInputClasses = ({
  hasHeight,
  hasRadius = true,
  hasValidationStatus,
  isDefaultState,
  isDisabled,
  isErrored,
  isValidated,
  hasNoBorder = false,
}: Record<string, boolean>): string =>
  classnames(
    'w-full appearance-none border px-20 py-10 text-14 leading-normal text-black outline-none transition-all duration-250 ease-in',
    {
      'rounded-pill': hasRadius,
      'h-48': hasHeight,
      'border-lightGrey hocus:border-black': isDefaultState,
      'border-success': isValidated,
      'formik-errored border-red': isErrored,
      'cursor-not-allowed border-grey bg-pearl text-grey': isDisabled,
      'pe-40': hasValidationStatus && (isErrored || isValidated),
      'border-none': hasNoBorder,
    },
  );
